/*
 * Element Brooklyn — first-party attribution capture (Phase 1a).
 *
 * Parses UTMs / click IDs / referrer / ShopMy off the landing URL and pings a
 * SAME-ORIGIN Cloudflare Worker at /_attr/track (sibling of the existing eb-geo
 * Worker at /_geo*). The WORKER sets the first-party `_eb_ft` (first-touch, ~90d)
 * and `_eb_lt` (last-touch, ~30d) cookies via Set-Cookie — server-set first-party
 * cookies survive Safari ITP, unlike document.cookie (capped to 24h on ad-decorated
 * landing pages). The Custom Web Pixel later reads `_eb_ft` at checkout_completed
 * and forwards it to Element Ops (so it also captures Shop Pay / Apple Pay orders
 * that cart attributes miss).
 *
 * This script deliberately does NOT write cookies itself (that would be ITP-capped)
 * and never blocks page render. No-ops when there's nothing worth recording.
 */
(function () {
  try {
    // ⚠️ `oppref` is the OpenAI (ChatGPT ads) click id. It was missing here, and
    // the eb-attr Worker DROPS it as well — proven by live probe: a request
    // carrying oppref + utm_source + gclid came back with cookies holding only
    // the latter two. Both halves have to allow it or the OpenAI CAPI has
    // nothing to attach to (0 of 1,731 events carried one). Ops is already
    // ready for it: `oppref` is in Attribution::OrderClassification::FIRST_PARTY_KEYS
    // and ServerConversions::Identity reads it from first_party_raw_payload.
    // ⚠️ It is deliberately NOT in CLICK_ID_KEYS on the ops side — those map to
    // fixed-width columns and are where the Sep 2026 ValueTooLong came from.
    var KEYS = [
      'utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'utm_id',
      'gclid', 'gbraid', 'wbraid', 'fbclid', 'fbc', 'fbp', 'ttclid', 'msclkid', 'oppref',
      // ⚠️ Reddit's click id, added 2026-09-12. The eb-attr Worker's KEYS must
      // carry it too — that is exactly how oppref was lost, see the note above.
      'rdt_cid',
      // Snapchat's click id (capitalised exactly like this on the URL), added
      // 2026-10-02 with the same two-halves rule. Ops stores it as `sccid`.
      'ScCid'
    ];

    var params = new URLSearchParams(window.location.search);
    var payload = {};
    KEYS.forEach(function (k) {
      var v = params.get(k);
      if (v) payload[k] = v;
    });

    var ref = document.referrer || '';
    var externalRef = !!ref && !isInternalReferrer(ref);

    // ShopMy affiliate signal. Names confirmed from the live tracker
    // (static.shopmy.us/Affiliates/sms_aff_clicktrack.js v2.1.0): it reads
    // ?smsclickid (and ?smscode for its auto-discount) and writes the
    // `sms_click_id` / `sms_click_time` cookies (30 days, per the script's
    // expires=30), a `sms_click_id` sessionStorage key and a `sms_click_id` cart
    // attribute. The old `shopmy_ref` cookie / ?shopmy / ?smref names were never
    // set by anyone; ?shopmy / ?smref are kept only as harmless fallbacks.
    // Sent as `shopmy` — the key the eb-attr Worker allow-lists and Ops maps to
    // orders.shopmy_ref.
    //
    // ⚠️ Send each click id ONCE. The Worker overwrites `_eb_lt` with whatever a
    // ping carries, and the ShopMy cookie lives 30 days (on mobile ShopMy also
    // re-adds ?smsclickid to the URL for ~10 min) — pinging it on every page
    // view would replace a later ad click's last-touch with a bare {shopmy}.
    // The cookie fallback is only used when localStorage works (so the dedupe
    // can hold); without it we rely on the landing URL alone.
    var SHOPMY_SENT_KEY = 'eb_attr_shopmy_sent';
    var storageOk = canUseStorage();
    var shopmy = params.get('smsclickid') || params.get('shopmy') || params.get('smref') ||
      (storageOk ? readCookie('sms_click_id') : '') || '';
    if (shopmy && storageOk && safeGet(SHOPMY_SENT_KEY) === shopmy) shopmy = '';

    var hasParams = Object.keys(payload).length > 0;
    if (!hasParams && !externalRef && !shopmy) return; // nothing to record

    if (externalRef) payload.referrer = ref;
    if (shopmy) payload.shopmy = shopmy;

    var qs = Object.keys(payload).map(function (k) {
      return encodeURIComponent(k) + '=' + encodeURIComponent(payload[k]);
    }).join('&');

    // credentials:include so the Worker can set/read the first-party cookies.
    fetch('/_attr/track?' + qs, { method: 'GET', credentials: 'include', keepalive: true })
      .then(function (res) {
        if (shopmy && res && res.ok) safeSet(SHOPMY_SENT_KEY, shopmy);
      })
      .catch(function () { /* attribution must never break the storefront */ });
  } catch (e) {
    /* swallow — never block render */
  }

  function readCookie(name) {
    var m = document.cookie.match('(?:^|; )' + name.replace(/([.$?*|{}()\[\]\\\/\+^])/g, '\\$1') + '=([^;]*)');
    return m ? decodeURIComponent(m[1]) : '';
  }

  // A referrer from our own properties (gallery./hospitality./jobs./creators.
  // etc. — the old `//host` substring check only matched www) or from the
  // checkout/payment/customer-account round-trip is NOT a new touch. Recording
  // it would make Ops classify the order as a 'referral' from ourselves.
  // Matches the host exactly or as a dot-suffix, so e.g. notshop.app is external.
  function isInternalReferrer(url) {
    // Declared inside the function: a `var` at IIFE level would still be
    // undefined here, because the try block above runs before it is assigned.
    var INTERNAL_REF_HOSTS = [
      'elementbrooklyn.com', 'myshopify.com', 'shopify.com', 'shop.app',
      'shopifycs.com', 'paypal.com', 'klarna.com', 'afterpay.com',
      'clearpay.co.uk', 'affirm.com', 'amazon-pay.com'
    ];
    var host;
    try { host = new URL(url).hostname.toLowerCase(); } catch (e) { return false; }
    if (host === window.location.hostname.toLowerCase()) return true;
    for (var i = 0; i < INTERNAL_REF_HOSTS.length; i++) {
      var d = INTERNAL_REF_HOSTS[i];
      if (host === d || host.slice(-(d.length + 1)) === '.' + d) return true;
    }
    return false;
  }

  // Private mode / disabled storage must not throw.
  function canUseStorage() {
    try {
      var k = '__eb_attr_probe';
      window.localStorage.setItem(k, '1');
      window.localStorage.removeItem(k);
      return true;
    } catch (e) { return false; }
  }
  function safeGet(k) {
    try { return window.localStorage.getItem(k); } catch (e) { return null; }
  }
  function safeSet(k, v) {
    try { window.localStorage.setItem(k, v); } catch (e) { /* ignore */ }
  }
})();
